<template>
  <ListComponent
    title="Customer Tariff V2"
    :totalCount="totalTariffStatistics.total_tariff"
  >
    <template #list>
      <v-row class="ma-0">
        <v-col cols="4" class="d-flex align-center px-0 pt-2">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchCustomerContracts"
          ></v-text-field>

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFilterDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(nonEmptyFilters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>

            <span v-if="Object.keys(nonEmptyFilters).length === 0"
              >Filters</span
            >

            <v-row v-else no-gutters>
              <v-col cols="12" class="font-weight-bold">Applied Filters</v-col>
              <v-col cols="12">
                <span
                  v-for="(value, key) in nonEmptyFilters"
                  :key="key"
                  class="text-capitalize"
                >
                  {{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>
          <Columns
            :headers="headers"
            :gridColInstance="gridColumnApi"
            localStorageKey="customerContractV2List"
          />
        </v-col>

        <v-col cols="4" class="px-0 d-flex justify-center">
          <v-tabs height="35" centered v-model="tab">
            <v-tabs-slider></v-tabs-slider>
            <v-tab>
              Active
              <v-badge
                v-if="totalTariffStatistics.active_tariff"
                top
                :content="totalTariffStatistics.active_tariff"
                overlap
                class="mb-4 ml-3"
              >
              </v-badge>
            </v-tab>
            <v-tab>
              Rejected
              <v-badge
                v-if="totalTariffStatistics.rejected_tariff"
                top
                :content="totalTariffStatistics.rejected_tariff"
                overlap
                class="mb-4 ml-3"
              >
              </v-badge>
            </v-tab>
            <v-tab>
              Closed
              <v-badge
                v-if="totalTariffStatistics.closed_tariff"
                top
                :content="totalTariffStatistics.closed_tariff"
                overlap
                class="mb-4 ml-3"
              >
              </v-badge>
            </v-tab>
          </v-tabs>
        </v-col>

        <v-col
          v-if="totalItems > 0"
          cols="4"
          class="d-flex justify-end align-center px-0 pt-2"
        >
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>

        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 192px)"
            class="ag-theme-alpine"
            id="customerContractV2Grid"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            @columnMoved="$bus.$emit('syncPreferences')"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="customerContractList"
          />
        </v-col>
      </v-row>

      <DetailsParent :resizable="true" v-model="displayDrawer" :options="{ width: 1200, borderSize: 3 }">
        <template #details>
          <CustomerContractV2Details
            :permissions="permissions"
            @refreshList="getCustomerContractList"
            :contractId="selectedContractId"
          />
        </template>
      </DetailsParent>

      <AdminCustomerContractV2Filters
        v-model="showFilterDialog"
        :bindingObject="filters"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(20)"
      />
      <RejectionRemarksDialog
        v-model="showCloseRemarksForm"
        :formDetails="contractDetails"
        :isRequiredRemarks="true"
        fieldKey="close_remarks"
        label="Close Remarks"
        @submitRemarks="closeRemarksSubmit"
      />
    </template>

    <template #dialogs>
      <RenewContractDialog
        v-model="renewDialog"
        :formDetails="renewObject"
        :formErrors="renewObjectErrors"
        @submitRemarks="renewContract"
        @removeFieldError="renewObjectErrors = {}"
      />
      <CustomerContractV2AddDialog
        v-model="showAddDialog"
        @submitted="onContractAdded"
      />
    </template>

    <template #actions>
      <v-btn
        v-if="permissions && permissions.add"
        @click="showAddDialog = true"
        color="primary"
        depressed
      >
        <v-icon>mdi-plus</v-icon>
        <span>Add Tariff</span>
      </v-btn>
    </template>
  </ListComponent>
</template>

<script>
import Columns from "@/components/General/Columns.vue";
import ListComponent from "@/components/General/ListComponent.vue";
import RenewContractDialog from "@/components/ModuleBased/dialogs/RenewContractDialog.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import AdminCustomerContractV2Button from "@/components/AgGridComponents/AdminCustomerContractV2Button.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import CustomerContractV2Details from "@/components/ModuleBased/details/CustomerContractV2Details.vue";
import AdminCustomerContractV2Filters from "@/components/ModuleBased/dialogs/filters/AdminCustomerContractV2Filters.vue";
import RejectionRemarksDialog from "@/components/ModuleBased/dialogs/RejectionRemarksDialog.vue";
import CustomerContractV2AddDialog from "@/components/ModuleBased/dialogForms/CustomerContractV2AddDialog.vue";

import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    AgGridVue,
    Pagination,
    AdminCustomerContractV2Button,
    agColumnHeader: CustomHeader,
    DetailsParent,
    CustomerContractV2Details,
    AdminCustomerContractV2Filters,
    RenewContractDialog,
    RejectionRemarksDialog,
    CustomerContractV2AddDialog,
    Columns,
  },
  data() {
    return {
      renewDialog: false,
      showAddDialog: false,
      showCloseRemarksForm: false,
      renewObject: {},
      renewObjectErrors: {},
      showFiltersDialog: false,

      sorting: {},
      headers: [
        {
          headerName: "Tariff ID",
          field: "contract_id",
          sortable: true,
          minWidth: 100,
        },
        {
          headerName: "Customer Code",
          field: "customer_code",
          tooltipField: "customer_code",
          sortable: true,
          minWidth: 110,
        },
        {
          headerName: "Customer",
          field: "customer_name",
          tooltipField: "customer_name",
          sortable: true,
          minWidth: 300,
        },
        {
          headerName: "Start Date",
          field: "start_date",
          sortable: true,
          minWidth: 120,
          tooltipValueGetter: (params) => {
            return this.$globals.formatDate(params.value);
          },
          valueFormatter: (params) => {
            return this.$globals.formatDate(params.value);
          },
        },
        {
          headerName: "End Date",
          field: "end_date",
          sortable: true,
          minWidth: 120,
          tooltipValueGetter: (params) => {
            return this.$globals.formatDate(params.value);
          },
          valueFormatter: (params) => {
            return this.$globals.formatDate(params.value);
          },
        },
        {
          headerName: "Actions",
          field: "actions",
          pinned: "right",
          minWidth: 230,
          cellRendererFramework: "AdminCustomerContractV2Button",
        },
      ],
      customerContractList: [],

      selectedContractId: null,
      displayDrawer: false,
      formErrors: {},

      pageNo: 1,
      itemsPerPage: 20,
      totalItems: 0,

      filters: {},
      showFilterDialog: false,

      tab: 0,

      permissions: null,
      contractDetails: {},

      totalTariffStatistics: [],

      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
    nonEmptyFilters() {
      return Object.fromEntries(
        Object.entries(this.filters).filter(([key, value]) => {
          if (typeof value === "object" && value !== null) {
            return Object.keys(value).length > 0;
          }

          return value;
        })
      );
    },
  },
  watch: {
    tab() {
      this.getCustomerContractList({ offset: 0, limit: this.itemsPerPage });
      this.displayDrawer = false;
    },
  },

  methods: {
    checkFilters() {
      const filters = localStorage.getItem("tarrif_v2_filters");

      if (filters) {
        this.filters = JSON.parse(filters);
        if (this.filters.sorting) {
          this.sorting = { ...this.filters.sorting };
          this.headers.map((obj) => {
            if (obj.field in this.sorting) {
              if (this.sorting[obj.field] == false) {
                obj.sort = "desc";
              } else if (this.sorting[obj.field]) {
                obj.sort = "asc";
              }
            }
          });
        }
      }
    },
    setFilters() {
      if (
        this.filters &&
        Object.keys(this.filters).length > 0 &&
        Object.keys(this.filters).indexOf("ordering")
      ) {
        delete this.filters.ordering;
      }
      localStorage.setItem(
        "tarrif_v2_filters",
        JSON.stringify({ ...this.filters, sorting: this.sorting })
      );
    },
    showCloseContractDialog(id) {
      this.selectedContractId = id;
      this.showCloseRemarksForm = true;
    },
    closeRemarksSubmit() {
      this.$bus.$emit("showLoader", true);
      let payload = {
        id: this.selectedContractId,
        close_remarks: this.contractDetails.close_remarks,
      };

      this.$api.customerContractV2
        .closeCustomerContractV2(payload)
        .then((res) => {
          this.getCustomerContractList();
          this.$bus.$emit("showToastMessage", {
            message: "Tariff closed successfully!",
            color: "success",
          });
          this.$bus.$emit("showLoader", false);
          this.showCloseRemarksForm = false;
          this.selectedContractId = null;
          delete this.contractDetails.close_remarks;
        })
        .catch((err) => {
          this.formErrors = err.data;
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: `${
              err.data && err.data.non_field_errors
                ? err.data.non_field_errors.join()
                : "Couldn't update Tariff status!"
            }`,
            color: "error",
          });
          this.$bus.$emit("showLoader", false);
        });
    },
    resizeGrid() {
      if (this.gridApi) {
        setTimeout(() => {
          this.gridApi.sizeColumnsToFit();
        }, 100);
      }
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(20);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchCustomerContracts() {
      this.pageNo = 1;
      this.getCustomerContractList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getCustomerContractList();
    },
    prevPage() {
      this.pageNo--;
      this.getCustomerContractList();
    },
    nextPage() {
      this.pageNo++;
      this.getCustomerContractList();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },
    getCustomerContractList(params = {}) {
      this.$bus.$emit("showLoader", true);
      let laneId;
      const laneObject = Object.assign({}, this.filters.lane);
      if (this.filters && this.filters.lane) {
        laneId = this.filters.lane.id;
        delete this.filters.lane;
        this.filters.lane = laneId;
      }
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        status:
          this.tab == 0
            ? "Active"
            : this.tab == 1
            ? "Rejected"
            : "Closed",
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.setFilters();
      this.$api.customerContractV2
        .getCustomerContractV2List(params)
        .then((res) => {
          this.customerContractList = res.data;
          this.totalItems = res.count;

          delete params.limit;
          delete params.offset;
          delete params.status;

          this.getTotalTariffCount(params);
          this.filters.lane = laneObject;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    viewContract(id) {
      this.selectedContractId = id;
      this.displayDrawer = true;
    },
    onContractAdded(newContract) {
      this.getCustomerContractList();
      if (newContract && newContract.id) {
        this.viewContract(newContract.id);
      }
    },
    checkQuery() {
      if ("active_tab" in this.$route.query) {
        this.tab = parseInt(this.$route.query.active_tab);
      }
    },
    showRenewDialog(id) {
      this.selectedContractId = id;
      this.renewDialog = true;
    },
    renewContract() {
      const params = {
        id: this.selectedContractId,
        data: this.renewObject,
      };
      this.$api.customerContractV2
        .renewContract(params)
        .then((res) => {
          this.renewDialog = false;
          this.getCustomerContractList();
          this.$bus.$emit("showToastMessage", {
            message: "Tariff Renewed Successfully!",
            color: "success",
          });
          this.$bus.$emit("showLoader", false);
          this.renewObject.start_date = null;
          this.renewObject.end_date = null;
        })
        .catch((err) => {
          console.error(err);
          this.renewObjectErrors = err.data;
          this.$bus.$emit("showToastMessage", {
            message: `${
              err.data && err.data.non_field_errors
                ? err.data.non_field_errors.join()
                : "Something went wrong!"
            }`,
            color: "error",
          });
          this.$bus.$emit("showLoader", false);
        });
    },
    getTotalTariffCount(params) {
      this.$api.customerContractV2
        .getTariffStatistics(params)
        .then((res) => {
          this.totalTariffStatistics = res.data;
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Couldn't load Tariff statistics!",
            color: "error",
          });
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("contract");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.permissions = { ...permissions };
        this.checkQuery();
      }
    }
  },
  mounted() {
    this.checkFilters();
    this.getTotalTariffCount();
    this.getCustomerContractList();
  },
};
</script>
